import React, { useState, useEffect } from 'react';
import data from './data/players_details.json';

const mockPlayers = data;

const compareAttributes = (guess, target) => {
  // Calculate age from birthDate
  const getAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const guessAge = getAge(guess.birthDate);
  const targetAge = getAge(target.birthDate);

  return {
    age: {
      correct: guessAge === targetAge,
      direction: guessAge < targetAge ? 'higher' : guessAge > targetAge ? 'lower' : 'equal',
      value: guessAge
    },
    country: {
      correct: guess.countryCode === target.countryCode,
      direction: 'equal',
      value: guess.countryCode
    },
    club: {
      correct: guess.clubName === target.clubName,
      direction: 'equal',
      value: guess.clubName
    },
    position: {
      correct: guess.position === target.position,
      direction: 'equal',
      value: guess.position
    }
  };
};

function GuessWho() {
  const [targetPlayer, setTargetPlayer] = useState(null);
  const [input, setInput] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [win, setWin] = useState(false);

  useEffect(() => {
    const random = mockPlayers[Math.floor(Math.random() * mockPlayers.length)];
    setTargetPlayer(random);
  }, []);

  const handleGuess = () => {
    const guessedPlayer = mockPlayers.find(
      (p) => p.displayName.toLowerCase() === input.toLowerCase()
    );
    if (!guessedPlayer) return;

    const feedback = compareAttributes(guessedPlayer, targetPlayer);
    const newGuess = { ...guessedPlayer, feedback };
    // Add new guess to the beginning of the array
    const newGuesses = [newGuess, ...guesses];

    setGuesses(newGuesses);
    setInput("");

    if (guessedPlayer.displayName === targetPlayer.displayName) {
      setGameOver(true);
      setWin(true);
    } else if (newGuesses.length === 8) {
      setGameOver(true);
    }
  };

  // Function to reveal the answer and end the game
  const handleReveal = () => {
    setGameOver(true);
    setWin(false);
  };

  // Function to render country flag
  const renderFlag = (countryCode, flagUrl) => {
    return (
      <img 
        src={flagUrl} 
        alt={countryCode} 
        className="me-2 ms-2" 
        style={{ width: '24px', height: 'auto' }}
      />
    );
  };

  // Function to render club logo
  const renderClubLogo = (clubName, clubPictureUrl) => {
    return (
      <img 
        src={clubPictureUrl} 
        alt={clubName} 
        className="me-2" 
        style={{ width: '24px', height: 'auto' }}
      />
    );
  };

  // Function to render direction indicators using text
  const renderDirectionIndicator = (attribute, feedback) => {
    if (feedback.correct) {
      return "✓";
    } else if (attribute === 'age' && feedback.direction !== 'equal') {
      return feedback.direction === 'higher' ? 
        "↑ HIGHER" : 
        "↓ LOWER";
    }
    return null;
  };

  return (
    <div className="mainbody container mt-4">
      <h1 className="text-center mb-3">Guess Who - StocksFC Edition</h1>
      
      {/* Game instructions */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">How to Play</h5>
          <p className="card-text">
            Try to guess the mystery football player in 8 attempts or less. Type a player name and click "Guess" to see if you're right.
            Green boxes show correct attributes. For age, arrows indicate if the mystery player is older (↑) or younger (↓) than your guess.
            Use these clues to narrow down your options with each guess!
          </p>
        </div>
      </div>

      {!gameOver && (
        <>
          <div className="input-group mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Guess a player..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              list="players"
            />
            <datalist id="players">
              {mockPlayers.map((p) => (
                <option key={p.displayName} value={p.displayName} />
              ))}
            </datalist>
            <button className="btn btn-primary" onClick={handleGuess}>
              Guess
            </button>
          </div>
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button 
              className="btn btn-outline-danger"
              onClick={handleReveal}
            >
              Reveal Answer
            </button>
            <span className="badge bg-secondary">
              {guesses.length}/8 Guesses Used
            </span>
          </div>
        </>
      )}

{gameOver && (
        <div className="text-center mt-4">
          <div className="d-flex justify-content-center align-items-center mb-3">
            <img 
              src={targetPlayer?.avatarPictureUrl} 
              alt={targetPlayer?.displayName} 
              className="rounded-circle me-2" 
              style={{ width: '64px', height: '64px' }}
            />
            <h2 className="mb-0">
              {win ? "🎉 You guessed it!" : `❌ The answer was ${targetPlayer?.displayName}`}
            </h2>
          </div>
          <div className="mt-3">
            <div className="d-flex justify-content-center">
              <div className="card" style={{ maxWidth: '400px' }}>
                <div className="card-body">
                    <div className="mb-2 text-center">
                    <strong className="me-2">Country:</strong>
                        {renderFlag(targetPlayer?.countryCode, targetPlayer?.flagUrl)}
                    </div>
                    <div className="d-flex align-items-center mb-2">
                        {renderClubLogo(targetPlayer?.clubName, targetPlayer?.clubPictureUrl)}
                        <strong className="me-1">Club: </strong> {targetPlayer?.clubName}
                    </div>
                    <div className="mb-2">
                        <strong>Position:</strong> {targetPlayer?.position}
                    </div>
                    <div className="mb-2">
                        <strong>Age: </strong> {targetPlayer && new Date().getFullYear() - new Date(targetPlayer.birthDate).getFullYear()}
                    </div>
                </div>
              </div>
            </div>
          </div>
          <button className="btn btn-secondary mt-3 mb-3" onClick={() => window.location.reload()}>
            Play Again
          </button>

        </div>
      )}

      <div className="row g-3">
        {guesses.map((guess, idx) => (
          <div className="col-sm-6 mb-3" key={idx}>
            <div className="card">
              <div className="card-header bg-light d-flex align-items-center">
                <div className="me-2">
                  <img 
                    src={guess.avatarPictureUrl} 
                    alt={guess.displayName} 
                    className="rounded-circle" 
                    style={{ width: '32px', height: '32px' }}
                  />
                </div>
                <div>
                  <strong>Guess {guesses.length - idx}/8:</strong> {guess.displayName}
                </div>
              </div>
              <div className="card-body">
                <div className="row g-2">   
                    {Object.entries(guess.feedback).map(([key, feedback]) => (
                    <div
                        key={key}
                        className={`col-6 p-2 rounded d-flex justify-content-between align-items-center ${
                        feedback.correct ? "bg-success text-white" : "bg-danger text-white"
                        }`}
                    >
                        <div className="d-flex align-items-center">
                        <strong className="me-1">
                            {key.charAt(0).toUpperCase() + key.slice(1)}:
                        </strong>

                        {key === 'country' && (
                            <>
                            <span className="ms-1">{renderFlag(guess.countryCode, guess.flagUrl)}</span>
                            </>
                        )}

                        {key === 'club' && (
                            <>
                            <span className="me-2">{renderClubLogo(guess.clubName, guess.clubPictureUrl)}</span>
                            <span>{feedback.value}</span>
                            </>
                        )}

                        {key !== 'country' && key !== 'club' && (
                            <span className="ms-1">{feedback.value}</span>
                        )}
                        </div>
                        
                        <div className="ms-2 fw-bold" style={{ fontSize: '1.1rem' }}>
                        {renderDirectionIndicator(key, feedback)}
                        </div>
                    </div>
                    ))}              
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>      
    </div>
  );
}

export default GuessWho;